// 261028 행동 로그 — 순수 함수(브라우저·스토어 없이 node --test). 화면 연결은 actionLog.js.
// 서버(ActionLogRules)가 다시 거르지만 앞단에서 먼저 줄여 보낸다: 화면 경로는 틀만, 글자는 숫자 7자리↑·메일을 가린다.
// 전화번호는 어디에도 싣지 않는다(tel: 링크는 '전화'라는 종류만).

export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const MAX_QUEUE = 200;
export const FLUSH_SIZE = 20;
export const MAX_BATCH = 50;
export const SEARCH_COALESCE_MS = 3000;

const LONG_NUMBER = /\d(?:[\s.-]?\d){6,}/g;
const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
// 제어 문자 → 공백(정규식에 제어 문자를 쓰면 eslint no-control-regex 에 걸려 문자 코드로 거른다)
const stripControl = s =>
  Array.from(s, ch => {
    const c = ch.codePointAt(0);
    return c < 32 || c === 127 || c === 0x2028 || c === 0x2029 ? ' ' : ch;
  }).join('');

// 화면 주소 쿼리 중 남길 것 — 업체·글·분류처럼 사람이 아닌 대상과 유입 경로만(이름·전화 같은 건 넣지 않는다).
export const QUERY_KEEP = ['category', 'cat', 'tab', 'code', 'businessNo', 'biz', 'postNo', 'churchNo', 'fromBusinessNo', 'radius', 'quick', 'arrival', 'utm_source', 'utm_medium', 'utm_campaign'];
const QUERY_TARGETS = ['businessNo', 'code', 'postNo', 'churchNo', 'biz'];

// 길안내 앱·지도 — '바깥 연결' 중 길찾기로 센다.
const MAP_SCHEMES = /^(kakaomap|nmap|tmap|kakaonavi(-sdk)?|navermap):/; // kakaonavi-sdk = 사업장 상세 '카카오내비'
const MAP_HOSTS = /(^|\.)(map\.kakao\.com|map\.naver\.com|tmap\.co\.kr)$/;

/** 글자 정리 — 제어 문자·겹 공백 정리, 메일·긴 숫자 가림, 글자 수(코드포인트) 자르기. */
export const maskText = (s, max = 40) => {
  if (s == null) return null;
  let t = stripControl(String(s)).replace(/\s+/g, ' ').trim();
  if (!t) return null;
  t = t.replace(EMAIL, '[메일]').replace(LONG_NUMBER, '[번호]');
  const cps = Array.from(t);
  return cps.length > max ? cps.slice(0, max).join('') : t;
};

/** 앱(안드·iOS) / 웹(폰·PC). 앱 여부는 앱 UA 표식을 아는 isApp() 결과를 받는다. */
export const platformOf = (isApp, ua = '') => {
  const s = String(ua || '').toLowerCase();
  const ios = /iphone|ipad|ipod/.test(s);
  if (isApp) return ios ? 'APP_IOS' : 'APP_AOS';
  return /iphone|ipad|ipod|android|mobile/.test(s) ? 'WEB_MOBILE' : 'WEB_PC';
};

/**
 * 화면(라우트) → { page: 경로 틀, targetType·targetId: 무엇을 보는지, props: 남길 쿼리 }.
 * 경로 변수 중 …No·…Id·code 를 대상으로 삼고, 없으면 쿼리(?businessNo=·?code=)에서 찾는다.
 */
export const routeInfo = route => {
  if (!route) return { page: null, targetType: null, targetId: null, props: {} };
  const matched = route.matched && route.matched.length ? route.matched[route.matched.length - 1] : null;
  let page = matched && matched.path ? matched.path : route.path || null;
  if (page && !(matched && matched.path)) page = String(page).replace(LONG_NUMBER, ':num');
  let targetType = null;
  let targetId = null;
  const params = route.params || {};
  for (const k of Object.keys(params)) {
    const v = params[k];
    if (v == null || Array.isArray(v) || v === '') continue;
    if (/(No|Id)$/.test(k) || /^code$/i.test(k)) {
      targetType = k;
      targetId = String(v).slice(0, 64);
      break;
    }
  }
  const props = {};
  const q = route.query || {};
  for (const k of QUERY_KEEP) {
    const v = q[k];
    if (v == null || Array.isArray(v) || String(v) === '') continue;
    const t = maskText(v, 40);
    if (t) props[k] = t;
  }
  if (!targetType) {
    for (const k of QUERY_TARGETS) {
      if (props[k]) {
        targetType = k;
        targetId = props[k];
        break;
      }
    }
  }
  return { page, targetType, targetId, props };
};

/**
 * 링크 → 바깥 연결 종류. 같은 사이트 안 이동·자바스크립트 링크는 null(화면 열람이 센다).
 * TEL·SMS·MAIL 은 번호·주소를 싣지 않고 종류만. 웹 주소는 호스트만(경로·쿼리에 개인정보가 섞일 수 있다).
 */
export const classifyHref = (href, origin = '') => {
  if (!href) return null;
  const h = String(href).trim();
  const lower = h.toLowerCase();
  if (lower.startsWith('tel:')) return { kind: 'TEL', host: null };
  if (lower.startsWith('sms:') || lower.startsWith('smsto:')) return { kind: 'SMS', host: null };
  if (lower.startsWith('mailto:')) return { kind: 'MAIL', host: null };
  if (lower.startsWith('javascript:') || lower.startsWith('#')) return null;
  const scheme = (lower.match(/^([a-z][a-z0-9+.-]*):/) || [])[1];
  if (scheme && MAP_SCHEMES.test(lower)) return { kind: 'MAP', host: scheme };
  if (scheme === 'http' || scheme === 'https') {
    let host = '';
    try {
      host = new URL(h).hostname.toLowerCase();
    } catch {
      return null;
    }
    let own = '';
    try {
      own = origin ? new URL(origin).hostname.toLowerCase() : '';
    } catch {
      own = '';
    }
    if (own && host === own) return null;
    return { kind: MAP_HOSTS.test(host) ? 'MAP' : 'LINK', host };
  }
  if (scheme) return { kind: 'APP', host: scheme };
  return null;
};

/** '종류:번호' 꼴 표식(data-ev-imp="businessNo:42") → { targetType, targetId }. */
export const parseTarget = s => {
  if (!s) return { targetType: null, targetId: null };
  const t = String(s);
  const i = t.indexOf(':');
  if (i <= 0) return { targetType: 'AD', targetId: t.slice(0, 64) };
  return { targetType: t.slice(0, i).slice(0, 24), targetId: t.slice(i + 1).slice(0, 64) || null };
};

/** 무작위 기기 값 — 저장소에 한 번 만들어 두고 계속 쓴다(지우면 새 기기로 센다). */
export const anonIdFrom = (storage, makeId) => {
  const KEY = 'jump_anon_id';
  try {
    const v = storage.getItem(KEY);
    if (v && /^[0-9A-Za-z-]{8,36}$/.test(v)) return v;
    const n = makeId();
    storage.setItem(KEY, n);
    return n;
  } catch {
    return null;
  }
};

/** 세션 — 30분 쉬면 새 값. { id, fresh } 를 돌려주고 마지막 활동 시각을 갱신한다. */
export const touchSession = (storage, now, makeId) => {
  const KEY = 'jump_al_session';
  let cur = null;
  try {
    cur = JSON.parse(storage.getItem(KEY) || 'null');
  } catch {
    cur = null;
  }
  const fresh = !cur || !cur.id || typeof cur.last !== 'number' || now - cur.last > SESSION_IDLE_MS;
  const id = fresh ? makeId() : cur.id;
  try {
    storage.setItem(KEY, JSON.stringify({ id, last: now }));
  } catch {
    /* 저장 막힘 — 이번 값만 쓴다 */
  }
  return { id, fresh };
};

/**
 * 보낼 줄. 상한(200)을 넘으면 오래된 것부터 버린다.
 * 검색은 3초 안에 이어 친 것(앞 검색어를 늘이거나 줄인 것, 한글 조합 중인 마지막 글자는 빼고 비교)을 마지막 하나로 합친다 — '치'·'치킨'·'치킨집', 'ㄱ'·'간ㅍ'·'간판'이 따로 쌓이지 않게.
 */
export const createQueue = (max = MAX_QUEUE) => {
  let items = [];
  return {
    push(ev, now = Date.now()) {
      if (ev.type === 'SEARCH' && items.length) {
        const last = items[items.length - 1];
        const a = last.label || '';
        const b = ev.label || '';
        // 한글은 치는 동안 마지막 글자가 조합 중이다('간ㅍ'→'간판') — 마지막 글자는 빼고 앞이 이어지는지 본다
        const head = s => Array.from(s).slice(0, -1).join('');
        if (last.type === 'SEARCH' && now - (last._at || 0) <= SEARCH_COALESCE_MS && (b.startsWith(head(a)) || a.startsWith(head(b)))) {
          items[items.length - 1] = { ...ev, _at: now };
          return;
        }
      }
      items.push({ ...ev, _at: now });
      if (items.length > max) items = items.slice(items.length - max);
    },
    /** 보낼 몫을 꺼낸다(최대 n개). 내부 표식(_at)은 떼고. */
    take(n = MAX_BATCH) {
      const out = items.slice(0, n).map(({ _at, ...rest }) => rest);
      items = items.slice(n);
      return out;
    },
    clear() {
      items = [];
    },
    get size() {
      return items.length;
    }
  };
};

/** 보낼지 정한다 — 스위치를 아직 못 받았으면 'wait'(쥐고 있기), 꺼졌으면 'drop', 켜졌으면 'send'. */
export const flushDecision = (flagsLoaded, flags) => {
  if (!flagsLoaded) return 'wait';
  return flags && flags.actionLogEnabled === true ? 'send' : 'drop';
};
